import { useCallback } from 'react';
import { useTranslation } from '@shared/core';
import { useStationeryTemplateCategoryTranslations } from '@apps/card/hooks/useStationeryTemplateCategoryTranslations';
import type { MegaNavLabel, MegaNavTextKey } from './megaNav.config';

/**
 * Resolves mega-nav label keys to strings. Category labels reuse the existing
 * `dashboardCategoryNav` translations; everything else is a `tq` literal, which must
 * sit lexically inside this hook for the translation extractor.
 */
export const useMegaNavLabels = () => {
  const { tq } = useTranslation('stationery');
  const getCategoryTitle = useStationeryTemplateCategoryTranslations('dashboardCategoryNav');

  const text: Record<MegaNavTextKey, string> = {
    ceremonyReception: tq`Ceremony & Reception`,
    allSaveTheDates: tq`All Save the Dates`,
    allInvitations: tq`All Invitations`,
    allThankYouCards: tq`All Thank You Cards`,
    allHolidayCards: tq`All Holiday Cards`,
    shopByFormat: tq`Shop by Format`,
    printed: tq`Printed`,
    digital: tq`Digital`,
    freeDigital: tq`Free Digital`,
    shopByFeature: tq`Shop by Feature`,
    foil: tq`Foil`,
    magnet: tq`Magnet`,
    postcard: tq`Postcard`,
    photo: tq`Photo`,
    shopByStyle: tq`Shop by Style`,
    botanical: tq`Botanical`,
    floral: tq`Floral`,
    elegantClassic: tq`Elegant & Classic`,
    destination: tq`Destination`,
    typographic: tq`Typographic`,
    rustic: tq`Rustic`,
    minimal: tq`Minimal`,
    vintage: tq`Vintage`,
    whimsical: tq`Whimsical`,
    suiteInserts: tq`Suite Inserts`,
    enclosureCards: tq`Enclosure Cards`,
    signs: tq`Signs`,
    menus: tq`Menus`,
    napkins: tq`Napkins`,
    servicesTools: tq`Services & Tools`,
    envelopes: tq`Envelopes`,
    freeSamplePack: tq`Free Sample Pack`,
    designConcierge: tq`Design Concierge`,
    wordingEtiquette: tq`Wording & Etiquette`,
    collectAddresses: tq`Collect Addresses`,
  };

  return useCallback(
    (label: MegaNavLabel): string => (typeof label === 'string' ? text[label] : getCategoryTitle(label.category)),
    // `text` is rebuilt each render from stable tq output; keying on tq keeps the callback stable per locale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tq, getCategoryTitle],
  );
};
